/**
 * The confirmation surface every admin mutation goes through.
 *
 * WHY A SHARED ONE. Each write on these screens is a real, audit-logged
 * decision — a KYC override, a role change, a release of someone's locked
 * Savings — and the two things that make those safe are identical everywhere:
 * the action is RESTATED IN WORDS before it is taken, and a reason is captured
 * as the audit record. Letting each screen build its own dialog is how one of
 * them ends up without the restatement, and the one without it is the one that
 * moves money in the wrong direction.
 *
 * Deliberately NOT a portal and deliberately no browser-storage of any kind.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from './Button';
import { ApiClientError } from '@/api/client';

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export function Modal({ open, title, onClose, children, footer }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* The backdrop closes, but only on the backdrop itself — a click that
          started inside the panel and drifted out must not discard the form. */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-lg rounded-2xl border border-white/[0.09] bg-brand-navy2 p-5 shadow-2xl"
      >
        <h2 className="font-display text-base font-bold tracking-tight">{title}</h2>
        <div className="mt-4">{children}</div>
        {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

interface ConfirmActionProps {
  open: boolean;
  title: string;
  /**
   * The action restated as a sentence an operator can check against what they
   * meant to do. Never a raw payload, never a bare signed number.
   */
  confirmLine: string;
  /** Extra inputs (an amount, a target role) rendered above the reason box. */
  children?: React.ReactNode;
  /** Blocks confirm while non-null — the caller's own field validation. */
  fieldError?: string | null;
  confirmLabel: string;
  danger?: boolean;
  pending: boolean;
  error: unknown;
  /** Rendered in place of the form once the write has succeeded. */
  success?: React.ReactNode;
  onConfirm: (reason: string) => void;
  onClose: () => void;
  /** Reason state is lifted so the caller can send it and reset it. */
  reason: string;
  onReasonChange: (next: string) => void;
  reasonError: string | null;
}

export function ConfirmAction({
  open,
  title,
  confirmLine,
  children,
  fieldError,
  confirmLabel,
  danger,
  pending,
  error,
  success,
  onConfirm,
  onClose,
  reason,
  onReasonChange,
  reasonError,
}: ConfirmActionProps) {
  /*
    Errors are only shown once the operator has tried, so an empty reason box
    is not red the instant the dialog opens.
  */
  const [touched, setTouched] = useState(false);
  const reasonId = useId();
  const firstFieldRef = useRef<HTMLTextAreaElement>(null);

  /*
    The reset is done DURING RENDER, not in an effect. An effect that calls
    setState on open schedules a second render pass carrying the previous
    dialog's `touched` — so a re-opened dialog flashes the last attempt's red
    validation for a frame. Adjusting state during render is the sanctioned
    React pattern for exactly this (state derived from a prop change), and it
    is what `react-hooks/set-state-in-effect` is pointing at.
  */
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setTouched(false);
  }

  // Focus IS an external system, so it stays in an effect.
  useEffect(() => {
    if (open) firstFieldRef.current?.focus();
  }, [open]);

  const blocked = reasonError !== null || (fieldError ?? null) !== null;

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        success ? (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              variant={danger ? 'danger' : 'primary'}
              size="sm"
              loading={pending}
              onClick={() => {
                setTouched(true);
                if (!blocked) onConfirm(reason.trim());
              }}
            >
              {confirmLabel}
            </Button>
          </>
        )
      }
    >
      {success ?? (
        <>
          <p className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-sm leading-relaxed text-white/85">
            {confirmLine}
          </p>

          {children && <div className="mt-4 space-y-3">{children}</div>}

          {touched && fieldError && <p className="mt-2 text-xs text-red-400">{fieldError}</p>}

          <div className="mt-4">
            <label htmlFor={reasonId} className="text-sm font-medium text-white/80">
              Reason
            </label>
            <textarea
              id={reasonId}
              ref={firstFieldRef}
              rows={2}
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Recorded in the audit log against your account"
              className={`mt-1 w-full resize-y rounded-lg border bg-brand-surface px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-brand-teal ${
                touched && reasonError ? 'border-red-500' : 'border-white/20'
              }`}
            />
            {touched && reasonError ? (
              <p className="mt-1 text-xs text-red-400">{reasonError}</p>
            ) : (
              <p className="mt-1 text-xs text-white/35">
                This is the only account of why, six weeks from now.
              </p>
            )}
          </div>

          {error != null && <ActionError error={error} />}
        </>
      )}
    </Modal>
  );
}

/**
 * A 403 here is not a bug and not transient — the server has RULED on the
 * caller's role. Saying "try again" would be false, so it is presented as the
 * answer it is, with the other statuses kept distinct.
 *
 * THE CODE IS READ BEFORE THE STATUS, because a status alone mis-describes the
 * refusals that matter most. `would_leave_zero_owners` is a 409, and the generic
 * 409 heading ("the record changed before this was applied") would send an
 * operator off to reload a page that is perfectly current — when what actually
 * happened is that the console refused to strand itself with no owner. Same for
 * `not_held`: nothing raced, someone simply got there first.
 */
const CODE_HEADINGS: Record<string, string> = {
  would_leave_zero_owners: 'That would leave the console with no owner',
  no_change: 'Nothing would change',
  not_held: 'That attribution has already been resolved',
  insufficient_match_credit: 'The account does not hold that much legacy credit',
  no_locked_savings: 'There are no locked Savings to release',
  not_pending: 'That proposal has already been resolved',
  not_settled: 'That bet has not settled, so there is nothing to correct',
  outcome_unchanged: 'The correction matches the settlement already recorded',
  version_mismatch: 'The bet was corrected by someone else first',
  unlock_provenance_unknown:
    'The original savings unlock cannot be traced, so it cannot be reversed',
  /*
    Both quarantine 409s mean SOMEONE ELSE GOT THERE FIRST, and the generic 409
    heading would send an operator to reload a page that is already current.
    The second is the more dangerous of the two to mis-state: a clear is already
    pending, so the fixture is still withheld and the right answer is to wait for
    its catalogue sync, not to press anything again.
  */
  fixture_already_quarantined: 'That fixture is already withheld from the board',
  fixture_quarantine_clear_pending:
    'A clear is already pending — the fixture stays withheld until its own catalogue sync rewrites it',
  fixture_quarantine_not_found: 'No quarantine is held on that fixture',
};

export function ActionError({ error }: { error: unknown }) {
  const api = error instanceof ApiClientError ? error : null;

  let heading = 'The action failed';
  const byCode = api ? CODE_HEADINGS[api.error.code] : undefined;
  if (byCode) heading = byCode;
  else if (api?.status === 403) heading = 'Your role does not permit this action';
  else if (api?.status === 404) heading = 'That record no longer exists';
  else if (api?.status === 409) heading = 'The record changed before this was applied';
  else if (api?.status === 422) heading = 'The server refused this change';

  return (
    <div className="mt-4 rounded-xl border border-red-500/35 bg-red-500/[0.07] p-3">
      <p className="text-sm font-semibold text-red-300">{heading}</p>
      <p className="mt-1 text-sm leading-relaxed text-white/65">
        {api ? api.error.message : 'Something went wrong. Nothing was written.'}
      </p>
      {api && <p className="mt-1 font-mono text-[11px] text-white/35">{api.error.code}</p>}
    </div>
  );
}
